/**
 * Admin layout spacing — single source of truth for AdminLayout shell spacing.
 * Update values here when admin header / page rhythm changes.
 */

/** Margin below the main admin header (AdminHeader) before page content. */
export const ADMIN_MAIN_HEADER_OFFSET_CLASS = 'mt-12';

/**
 * Margin below a page tab bar before tab panel content.
 * Kept separate so tab rhythm can diverge from header rhythm if needed.
 */
export const ADMIN_TAB_CONTENT_OFFSET_CLASS = ADMIN_MAIN_HEADER_OFFSET_CLASS;

function joinAdminLayoutClasses(base, extraClasses = '') {
  const extra = (extraClasses || '').trim();
  return extra ? `${base} ${extra}` : base;
}

/** Combine main header offset with optional extra Tailwind classes. */
export function adminMainHeaderOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_MAIN_HEADER_OFFSET_CLASS, extraClasses);
}

/** Combine tab content offset with optional extra Tailwind classes. */
export function adminTabContentOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_TAB_CONTENT_OFFSET_CLASS, extraClasses);
}

/** Top spacing inside a tab panel below the tab bar. */
export const ADMIN_TAB_PANEL_CLASS = ADMIN_TAB_CONTENT_OFFSET_CLASS;

/** Standard detail tab panel — narrow container. */
export const ADMIN_TAB_CONTENT_CONTAINER_CLASS = adminTabContentOffsetClass('tp-container-small');

/** Detail tab panel — narrow container with min-w-0 (wide tables). */
export const ADMIN_TAB_CONTENT_CONTAINER_MIN_CLASS = adminTabContentOffsetClass(
  'min-w-0 tp-container-small',
);

/** Tab panel for flags / audit / transaction tables — full width (no tp-container-small). */
export const ADMIN_TAB_CONTENT_FULL_CLASS = adminTabContentOffsetClass('w-full min-w-0');

/** Tab panel for message / quote thread chat (centered, max width). */
export const ADMIN_TAB_CONTENT_WIDE_CLASS = adminTabContentOffsetClass(
  'mb-0 w-full max-w-3xl mx-auto',
);

const ADMIN_FULL_WIDTH_TAB_IDS = new Set([
  'flags',
  'flag',
  'logs',
  'audit',
  'audit-log',
  'history',
  'transactions',
]);

/** Overview stays narrow; flags / audit / history tables use full width. */
export function adminDetailTabContentClassName(activeTab, extraFullTabs = []) {
  if (ADMIN_FULL_WIDTH_TAB_IDS.has(activeTab) || extraFullTabs.includes(activeTab)) {
    return ADMIN_TAB_CONTENT_FULL_CLASS;
  }
  return ADMIN_TAB_CONTENT_CONTAINER_CLASS;
}

/** Margin below page/list titles (e.g. “Customers”) before filters/content. */
export const ADMIN_PAGE_TITLE_OFFSET_CLASS = 'mb-12';

/** Combine page title bottom spacing with optional extra Tailwind classes. */
export function adminPageTitleOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_PAGE_TITLE_OFFSET_CLASS, extraClasses);
}

/** Standard list page title — bottom spacing before filters. */
export const ADMIN_PAGE_TITLE_CLASS = ADMIN_PAGE_TITLE_OFFSET_CLASS;

/** Margin below search + filter toolbar before table/content. */
export const ADMIN_TOOLBAR_OFFSET_CLASS = 'mb-8';

/** Combine toolbar bottom spacing with optional extra Tailwind classes. */
export function adminToolbarOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_TOOLBAR_OFFSET_CLASS, extraClasses);
}

/** Standard list page search + filters row. */
export const ADMIN_TOOLBAR_CLASS = adminToolbarOffsetClass('px-4');

/** Margin below form action row (Save / Draft) at page bottom. */
export const ADMIN_FORM_FOOTER_OFFSET_CLASS = 'mb-12';

/** Combine form footer bottom spacing with optional extra Tailwind classes. */
export function adminFormFooterOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_FORM_FOOTER_OFFSET_CLASS, extraClasses);
}

/** Standard form page bottom action row — gap above buttons and page-bottom rhythm. */
export const ADMIN_FORM_FOOTER_CLASS = adminFormFooterOffsetClass('mt-12 px-4');

/** Margin below list table before pagination/footer. */
export const ADMIN_TABLE_OFFSET_CLASS = 'mb-12';

/** Combine table bottom spacing with optional extra Tailwind classes. */
export function adminTableOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_TABLE_OFFSET_CLASS, extraClasses);
}

/** Standard list page table container. */
export const ADMIN_TABLE_CLASS = adminTableOffsetClass('!bg-white lg:px-4 w-full');

/** Standard detail tab table container (flags, audit log, etc.). */
export const ADMIN_DETAIL_TABLE_CLASS = adminTableOffsetClass(
  '!bg-white lg:px-4 w-full min-w-0 max-w-full',
);

/** Margin below detail page profile header (avatar + metadata), before tabs. */
export const ADMIN_DETAIL_MAIN_HEADER_OFFSET_CLASS = 'mb-4';

/** Combine detail main header bottom spacing with optional extra Tailwind classes. */
export function adminDetailMainHeaderOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_DETAIL_MAIN_HEADER_OFFSET_CLASS, extraClasses);
}

/** Standard detail page profile header container (not the name/title line). */
export const ADMIN_DETAIL_MAIN_HEADER_CLASS = adminDetailMainHeaderOffsetClass('!bg-white');

/** Margin below a page section (KPI row, chart block, form). */
export const ADMIN_SECTION_OFFSET_CLASS = 'mb-12';

/** Combine section bottom spacing with optional extra Tailwind classes. */
export function adminSectionOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_SECTION_OFFSET_CLASS, extraClasses);
}

/** KPI / metric summary card (analytics, reports, dashboards). */
export const ADMIN_METRIC_PANEL_CLASS = '!bg-white lg:px-4 w-full';

/** Combine metric panel classes with optional extras. */
export function adminMetricPanelOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_METRIC_PANEL_CLASS, extraClasses);
}

/** Chart / report content panel (no table bottom margin). */
export const ADMIN_CONTENT_PANEL_CLASS = '!bg-white w-full flex flex-col rounded-xl';

/** Combine content panel classes with optional extras. */
export function adminContentPanelOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_CONTENT_PANEL_CLASS, extraClasses);
}

/** Form panel on settings / configuration pages. */
export const ADMIN_FORM_PANEL_CLASS = '!bg-white lg:px-4 w-full';

/** Compact add/edit shell — Trade Category form width (25rem). */
export const ADMIN_COMPACT_FORM_SHELL_CLASS = 'max-w-[25rem] mx-auto w-full min-w-0';

/** Compact add/edit form body — one field per row. */
export const ADMIN_COMPACT_FORM_BODY_CLASS = 'p-4 space-y-6 min-w-0';

/** Combine form panel classes with optional extras. */
export function adminFormPanelOffsetClass(extraClasses = '') {
  return joinAdminLayoutClasses(ADMIN_FORM_PANEL_CLASS, extraClasses);
}
